/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  EntityAssociateType,
  EntityType,
  FieldConfigType,
  FieldType,
} from "@/schemas/digital-files";
import type {
  EntityField,
  EntityModalState,
  FieldTypeName,
  Section,
} from "./types";
import type { RegistryType } from "@/schemas/digital-files/registry";
import type { UserType } from "@/types/entities/users";
import { parseCharacters } from "@/utils/format";
import dayjs from "dayjs";
import { AxiosError } from "axios";
import { filterTypesEnum } from "@/types/common/filters";
import { buildHierarchyStatusFilter } from "../home/utils/serialization";

export const ALLOWED_FILE_TYPES = [
  "pdf",
  "doc",
  "docx",
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "svg",
  "txt",
  "csv",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "json",
  "zip",
  "rar",
  "mpp",
  "mpt",
  "dwg",
  "dwf",
  "dxf",
];

export const getMimeTypes = (extensions: string[]): string[] => {
  const mimeMap: Record<string, string> = {
    pdf: "application/pdf",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    svg: "image/svg+xml",
    txt: "text/plain",
    csv: "text/csv",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ppt: "application/vnd.ms-powerpoint",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    json: "application/json",
    zip: "application/zip",
    rar: "application/x-rar-compressed",
    mpp: "application/vnd.ms-project",
    mpt: "application/vnd.ms-project",
    dwg: "image/vnd.dwg",
    dxf: "image/vnd.dxf",
    dwf: "model/vnd.dwf",
  };

  if (extensions.includes("*")) {
    return ["*/*"];
  }

  return extensions
    .map((ext) => mimeMap[ext.toLowerCase()])
    .filter((mime): mime is string => Boolean(mime));
};

export const getSectionsFromEntity = (data: EntityType) => {
  const sections: Section[] = [];
  if (data && data.fields) {
    for (const element of data.fields) {
      const sectionTitle = element.configuration?.section?.title || "";
      const sectionOrder = element.configuration?.section?.order || 0;

      if (!sections.find((sec) => sec.title === sectionTitle)) {
        sections.push({
          id: crypto.randomUUID(),
          title: sectionTitle,
          order: sectionOrder,
          fields: [element],
        });
      } else {
        const sectionIndex = sections.findIndex(
          (sec) => sec.title === sectionTitle,
        );
        sections[sectionIndex].fields.push(element);
      }
    }
  }
  return sections;
};

export const getSectionsFromRegistry = (data: RegistryType) => {
  const sections: Section[] = [];
  if (data && data.field_values) {
    for (const element of data.field_values) {
      const sectionTitle =
        element.entity_field?.configuration?.section?.title || "";
      const sectionOrder =
        element.entity_field?.configuration?.section?.order || 0;

      if (!sections.find((sec) => sec.title === sectionTitle)) {
        sections.push({
          id: crypto.randomUUID(),
          title: sectionTitle,
          order: sectionOrder,
          fields: [element.entity_field as FieldType],
        });
      } else {
        const sectionIndex = sections.findIndex(
          (sec) => sec.title === sectionTitle,
        );
        sections[sectionIndex].fields.push(element.entity_field as FieldType);
      }
    }
  }
  return sections;
};

export const getMappedFields = (sections: Section[]): FieldType[] => {
  return sections.flatMap((section, sectionIndex) =>
    section.fields.map((field, fieldIndex) => {
      const {
        id,
        name,
        type,
        field_type,
        is_required,
        default_value,
        configuration,
      } = field;

      // Build configuration object and filter out null values
      const configurationObject = {
        show_in_summary: configuration?.show_in_summary || false,
        section: {
          title: section.title,
          order: sectionIndex + 1,
        },
        key: configuration?.key,
        buttonPosition: configuration?.buttonPosition,
        placeholder: configuration?.placeholder,
        maxLength: configuration?.maxLength,
        buttonEnabled: configuration?.buttonEnabled ?? false,
        // Dropdown button: solo enviar si buttonEnabled es true
        ...(configuration?.buttonEnabled && {
          options_source: configuration?.options_source,
          ...(configuration?.options_source === "database" && {
            options_table: configuration?.options_table,
            options_column: configuration?.options_column,
          }),
          ...(configuration?.options_source === "manual" &&
            configuration?.options_dropdown && {
              options_dropdown: configuration.options_dropdown,
            }),
        }),
        // Campo select/local_list: según options_root_source, enviar tabla/columna O options
        ...(configuration?.options_root_source === "database" && {
          options_root_table: configuration?.options_root_table,
          options_root_column: configuration?.options_root_column,
        }),
        ...(configuration?.options_root_source === "manual" &&
          configuration?.options && {
            options: configuration?.options,
          }),
        options_root_source: configuration?.options_root_source,
        multipleSelection: configuration?.multipleSelection,
        can_expand: configuration?.can_expand,
        type: configuration?.type || type,
        decimals: configuration?.decimals,
        minDate: configuration?.minDate,
        maxDate: configuration?.maxDate,
        includeTime: configuration?.includeTime,
        format24: configuration?.format24,
        allowedDomains: configuration?.allowedDomains,
        noAllowedDomains: configuration?.noAllowedDomains,
        allowCountryNumber: configuration?.allowCountryNumber,
        documentType: configuration?.documentType,
        allowedFileTypes: configuration?.allowedFileTypes,
        allowMultipleFiles: configuration?.allowMultipleFiles,
        maxFileSize: configuration?.maxFileSize,
        ocrFile: configuration?.ocrFile,
        dataFile: configuration?.dataFile,
        interopInstitution: configuration?.interopInstitution,
        interopAction: configuration?.interopAction,
      };

      // Filter out null and undefined values, but preserve empty arrays for domain validations
      const filteredConfiguration = Object.fromEntries(
        Object.entries(configurationObject).filter(([key, value]) => {
          // Always keep allowedDomains and noAllowedDomains even if empty
          if (key === "allowedDomains" || key === "noAllowedDomains") {
            return Array.isArray(value);
          }
          return value != null;
        }),
      );

      return {
        id: id,
        name: name,
        field_type: field_type,
        is_required: is_required,
        default_value: default_value,
        configuration: filteredConfiguration,
        order: fieldIndex + 1,
      };
    }),
  );
};

export const getFieldTypeInfo = (field: FieldType) => {
  let fieldKey =
    field.configuration?.type?.toLowerCase() ||
    field.type?.toLowerCase() ||
    field.field_type?.toLowerCase() ||
    "text";
  if (field?.configuration?.documentType) {
    fieldKey = "identification";
  }
  const fieldTypeMap: Record<string, { label: string; icon: string }> = {
    text: { label: "Campo de texto", icon: "text" },
    number: { label: "Campo numérico", icon: "hashtag" },
    textarea: { label: "Área de texto (párrafo)", icon: "paragraph" },
    date: { label: "Fecha", icon: "calendar-clock" },
    datetime: { label: "Fecha y hora", icon: "calendar-clock" },
    datetime_range: { label: "Rango de fechas", icon: "calendars" },
    date_range: { label: "Rango de fechas", icon: "calendars" },
    email: { label: "Correo electrónico", icon: "envelope" },
    phone: { label: "Número telefónico", icon: "phone" },
    identification: { label: "Identificación", icon: "id-card" },
    file: { label: "Subida de archivos", icon: "file-arrow-up" },
    switch: { label: "Campo Switch", icon: "toggle-on" },
    select: { label: "Campo Select", icon: "list" },
    multiselect: { label: "Multi-selección", icon: "list" },
    interoperability: { label: "Interoperabilidad", icon: "diagram-nested" },
  };
  return (
    fieldTypeMap[fieldKey] || {
      label: "Campo",
      icon: "question",
    }
  );
};

export const getFieldTypeDB = (field: FieldTypeName) => {
  const fieldTypeMap: Record<FieldTypeName, string> = {
    text: "TEXT",
    number: "NUMBER",
    date: "DATE",
    date_range: "DATE",
    email: "EMAIL",
    phone: "PHONE",
    textarea: "TEXTAREA",
    select: "SELECT",
    multiselect: "MULTISELECT",
    identification: "TEXT",
    file: "URL",
    interoperability: "TEXT",
    switch: "SWITCH",
  };
  return fieldTypeMap[field] || "TEXT";
};

export const getModalValues = (field: FieldType): EntityField => {
  const configuration = field.configuration || ({} as FieldConfigType);

  const { type: configType, key, can_expand, ...configFields } = configuration;

  // Usar configType como fuente primaria, fallback a field.type para legacy
  const fieldType = (configType || field.type || "text") as FieldTypeName;

  return {
    id: field.id,
    key: key ?? (field.name ? parseCharacters(field.name.toLowerCase()) : ""),
    type: fieldType,
    name: field.name,
    required: field.is_required,
    defaultValue: field.default_value || undefined,
    // Normalizar can_expand a expandSelectedRecord
    ...(can_expand !== undefined &&
      can_expand !== null && { expandSelectedRecord: can_expand }),
    ...configFields,
  };
};

export const createField = (
  config: EntityField,
  selectedSection?: Section,
): FieldType => {
  const {
    id,
    type,
    name,
    required,
    defaultValue,
    expandSelectedRecord,
    configuration: entityConfiguration,
    // Extraer valores para el dropdown button (options)
    options_table,
    options_column,
    options_source,
    options_dropdown,
    buttonEnabled,
    buttonPosition,
    // Extraer valores para el campo select/local_list (options_root)
    options_root_table,
    options_root_column,
    options_root_list,
    options_root_source,
    multipleSelection,
    ...rest
  } = config;

  // Separate configuration properties from top-level normalized properties
  const configurationFields = { ...rest };
  // Remove known top-level properties that shouldn't be in configuration
  delete configurationFields.fieldType;

  // Aplanar entityConfiguration si existe (para evitar anidación)
  const flattenedEntityConfig =
    entityConfiguration && typeof entityConfiguration === "object"
      ? entityConfiguration
      : {};

  const finalType =
    multipleSelection && type === "select" ? "multiselect" : type;

  const result: FieldType = {
    id: id || crypto.randomUUID(),
    name,
    field_type: getFieldTypeDB(finalType as FieldTypeName),
    type: finalType,
    default_value: defaultValue || null,
    is_required: required ?? false,
    order: selectedSection ? selectedSection.fields.length + 1 : 0,
    configuration: {
      ...configurationFields,
      key: config.key,
      type: type,
      section: {
        order: selectedSection?.order || 1,
        title: selectedSection?.title || "",
      },
      ...flattenedEntityConfig,
      // Normalizar expandSelectedRecord a can_expand
      ...(expandSelectedRecord !== undefined &&
        expandSelectedRecord !== null && { can_expand: expandSelectedRecord }),
      ...(buttonEnabled && {
        buttonEnabled: buttonEnabled,
        buttonPosition: buttonPosition,
        ...(options_source && { options_source: options_source }),
        ...(options_source === "database" && {
          ...(options_table && { options_table: options_table }),
          ...(options_column && { options_column: options_column }),
        }),
        ...(options_source === "manual" &&
          options_dropdown &&
          Array.isArray(options_dropdown) &&
          options_dropdown.length > 0 && {
            options_dropdown: options_dropdown,
          }),
      }),
      ...(options_root_source === "database" && {
        ...(options_root_table && { options_root_table: options_root_table }),
        ...(options_root_column && {
          options_root_column: options_root_column,
        }),
      }),
      ...(options_root_source === "manual" &&
        options_root_list &&
        Array.isArray(options_root_list) &&
        options_root_list.length > 0 && {
          options: options_root_list,
        }),
      ...(options_root_source && { options_root_source: options_root_source }),
      ...(multipleSelection !== undefined &&
        multipleSelection !== null && { multipleSelection: multipleSelection }),
    },
  };

  return result;
};

export const getDocumentType = (
  documentType: string | undefined | null,
): string => {
  if (!documentType) return "dui";
  const docType = documentType.toLowerCase();
  if (docType === "auto") return "dui";
  return docType;
};

export const getEntityOperationModal = (
  operation: EntityModalState,
  version: number,
) => {
  if (operation === "deactivate") {
    return {
      title: "¿Estás seguro que quieres desactivar?",
      description: "Una vez desactivada, los registros no estarán disponibles.",
      acceptText: "Desactivar",
    };
  }
  if (operation === "reactivate") {
    return {
      title: "¿Estás seguro que quieres reactivar?",
      description: "Una vez activada, los registros estarán disponibles.",
      acceptText: "Reactivar",
    };
  }
  if (operation === "activate" && version > 1) {
    return {
      title: "¿Estás seguro que quieres actualizar?",
      description: `Al actualizar, se archiva la versión actual y la versión ${version} se convierte en la versión activa.`,
      acceptText: "Actualizar",
    };
  }
  return {
    title: "¿Estás seguro que quieres activar?",
    description: "Una vez activada, estará disponible para crear registros.",
    acceptText: "Activar",
  };
};

export const getAssociate = (user: UserType): EntityAssociateType => {
  return {
    applicant_id: user.id,
    applicant: {
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
    },
    role: null,
  };
};

const formatValue = (
  type: string | undefined,
  value: unknown,
  fallback: string,
): string => {
  // check if is a file and is coming serialized in a string
  if ((type === "file" || type === "url") && typeof value === "string") {
    try {
      const parsedFile = JSON.parse(value);
      if (
        typeof parsedFile === "object" &&
        parsedFile !== null &&
        "file_name" in parsedFile
      ) {
        return String((parsedFile as { file_name: string }).file_name);
      }
    } catch (e) {
      console.warn("Error parsing file value:", e);
    }
  }

  if (typeof value === "string" || typeof value === "number") {
    if (type === "date" || type === "datetime") {
      if (typeof value === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        return value;
      }
      const format = type === "date" ? "DD/MM/YYYY" : "DD/MM/YYYY HH:mm";
      return dayjs(value).isValid() ? dayjs(value).format(format) : fallback;

      // return dayjs(value).isValid()
      //   ? dayjs(value).format(
      //       type === "date" ? "DD/MM/YYYY" : "DD/MM/YYYY HH:mm",
      //     )
      //   : fallback;
    }
    return String(value);
  }

  // check if is a file and is coming serialized in a string
  // if (type === "file" && typeof value === "string") {
  //   try {
  //     const parsedFile = JSON.parse(value);
  //     if (
  //       typeof parsedFile === "object" &&
  //       parsedFile !== null &&
  //       "file_name" in parsedFile
  //     ) {
  //       return String((parsedFile as { file_name: string }).file_name);
  //     }
  //   } catch (e) {
  //     console.warn("Error parsing file value:", e);
  //   }
  // }

  // if (typeof value === "string" || typeof value === "number") {
  //   if (type === "date" || type === "datetime") {
  //     return dayjs(value).isValid()
  //       ? dayjs(value).format(
  //           type === "date" ? "DD/MM/YYYY" : "DD/MM/YYYY HH:mm",
  //         )
  //       : fallback;
  //   }
  //   return String(value);
  // }

  if (
    type === "date_range" &&
    typeof value === "object" &&
    value !== null &&
    "start" in value &&
    "end" in value
  ) {
    try {
      if (
        value.start &&
        value.end &&
        typeof value === "object" &&
        "start" in value &&
        "end" in value &&
        typeof value.start === "string" &&
        typeof value.end === "string" &&
        dayjs(value.start).isValid() &&
        dayjs(value.end).isValid()
      ) {
        return `${dayjs(value.start).format("DD/MM/YYYY")} - ${dayjs(
          value.end,
        ).format("DD/MM/YYYY")}`;
      } else {
        return fallback;
      }
    } catch {
      return fallback;
    }
  }
  // list item object with key and label
  if (
    typeof value === "object" &&
    value !== null &&
    "key" in value &&
    "label" in value
  ) {
    return String(value.label);
  }

  if (
    type === "identification" &&
    typeof value === "object" &&
    value !== null &&
    "value" in value
  ) {
    return value.value as string;
  }

  // search some file name
  if (typeof value === "object" && value !== null && "file_name" in value) {
    return String((value as { file_name: string }).file_name);
  }

  // object with multiple keys/values
  if (typeof value === "object" && value !== null) {
    const values = Object.values(value).filter(
      (val) => val !== null && val !== undefined && val !== "",
    );
    if (values.length > 0) {
      return values.join(", ");
    }
  }
  return "";
};

export const getFormattedFieldValue = (
  type: string | undefined,
  value: unknown | Array<unknown>,
  defaultString = "No asignado",
): string[] => {
  if (Array.isArray(value)) {
    // arreglo de valores
    const values = value
      .map((val) => formatValue(type, val, defaultString))
      .filter((v): v is string => v !== null);
    if (values.length > 0) {
      return [...values];
    }
  } else {
    const formatted = formatValue(type, value, defaultString);
    if (formatted) {
      return [formatted];
    }
  }
  return [defaultString];
};

export const getCodeError = (error: unknown) => {
  if (error instanceof AxiosError) {
    const errors = error.response?.data?.errors;
    if (
      errors &&
      errors.code &&
      Array.isArray(errors.code) &&
      errors.code.length > 0
    ) {
      return errors.code[0];
    }
  }
};

/**
 * Filters formality requests based on a search string
 * Searches across name, code, status, applicant name and email
 *
 * @param requestList - Array of formality requests to filter
 * @param searchString - Search term to filter by
 * @returns Filtered array of formality requests
 */
export const filterFormalityRequests = <
  T extends {
    name?: string | null;
    code?: string | null;
    status?: string | null;
    updated_at?: string | null;
    applicant?: {
      first_name?: string | null;
      last_name?: string | null;
      email?: string | null;
    } | null;
  },
>(
  requestList: T[],
  searchString?: string,
  queryFilters?: object,
): T[] => {
  if (queryFilters && Object.keys(queryFilters).length > 0) {
    // filter by status and date
    if (
      "status" in queryFilters &&
      typeof queryFilters["status"] === "string" &&
      queryFilters["status"].trim() !== ""
    ) {
      const validStatus = queryFilters["status"]
        .split("|")
        .flatMap((group) => {
          const [, children] = group.split(":");
          if (!children) return [];
          return children.split(",");
        })
        .map((s) => s.trim());

      requestList = requestList.filter(
        (request) => request.status && validStatus.includes(request.status),
      );
    }

    // Handle date range filters - expect updated_at with format "from,to"
    if (
      "updated_at" in queryFilters &&
      typeof queryFilters["updated_at" as keyof typeof queryFilters] ===
        "string"
    ) {
      const dateValue = queryFilters[
        "updated_at" as keyof typeof queryFilters
      ] as string;
      const dateRange = dateValue.split(",").map((d) => d.trim());

      requestList = requestList.filter((request) => {
        if (!request.updated_at) return false;
        const updatedAt = dayjs(request.updated_at);

        if (dateRange.length === 2) {
          // Both from and to - filter by range (inclusive)
          const startDate = dayjs(dateRange[0]);
          const endDate = dayjs(dateRange[1]);
          return (
            (updatedAt.isAfter(startDate) ||
              updatedAt.isSame(startDate, "day")) &&
            (updatedAt.isBefore(endDate) || updatedAt.isSame(endDate, "day"))
          );
        } else if (dateRange.length === 1 && dateRange[0]) {
          // Single date - exact match
          const date = dayjs(dateRange[0]);
          return (
            updatedAt.isSame(date, "day") || updatedAt.isAfter(date, "day")
          );
        }
        return true;
      });
    }
  }
  if (!searchString || searchString.trim() === "") {
    return requestList;
  }

  const searchLower = searchString.toLowerCase().trim();
  return requestList.filter((request) => {
    // Buscar en nombre del trámite
    if (request.name?.toLowerCase().includes(searchLower)) {
      return true;
    }

    // Buscar en código/ID
    if (request.code?.toLowerCase().includes(searchLower)) {
      return true;
    }

    // Buscar en estado
    if (request.status?.toLowerCase().includes(searchLower)) {
      return true;
    }

    // Buscar en nombre del solicitante
    const applicantFullName =
      `${request.applicant?.first_name ?? ""} ${request.applicant?.last_name ?? ""}`.toLowerCase();
    if (applicantFullName.includes(searchLower)) {
      return true;
    }

    // Buscar en email del solicitante
    if (request.applicant?.email?.toLowerCase().includes(searchLower)) {
      return true;
    }

    return false;
  });
};

/**
 * Sort formality requests by a specific field
 * @param requestList - Array of formality requests to sort
 * @param columnId - Column ID to sort by
 * @param descending - Whether to sort in descending order
 * @returns Sorted array of formality requests
 */
export const sortFormalityRequests = <
  T extends {
    name?: string | null;
    code?: string | null;
    status?: string | null;
    created_at?: string | null;
    applicant?: {
      first_name?: string | null;
      last_name?: string | null;
    } | null;
  },
>(
  requestList: T[],
  columnId: string,
  descending: boolean,
): T[] => {
  return [...requestList].sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    switch (columnId) {
      case "name":
        aValue = a.name?.toLowerCase() ?? "";
        bValue = b.name?.toLowerCase() ?? "";
        break;
      case "code":
        aValue = a.code ?? "";
        bValue = b.code ?? "";
        break;
      case "status":
        aValue = a.status?.toLowerCase() ?? "";
        bValue = b.status?.toLowerCase() ?? "";
        break;
      case "applicant":
        aValue = a.applicant?.first_name?.toLowerCase() ?? "";
        bValue = b.applicant?.first_name?.toLowerCase() ?? "";
        break;
      case "elapsed_days":
        aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
        bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return descending ? 1 : -1;
    if (aValue > bValue) return descending ? -1 : 1;
    return 0;
  });
};

/**
 * Sort resolutions by a specific field
 * @param resolutionList - Array of resolutions to sort
 * @param columnId - Column ID to sort by
 * @param descending - Whether to sort in descending order
 * @returns Sorted array of resolutions
 */
export const sortResolutions = <
  T extends {
    display_name?: string | null;
    display_code?: string | null;
    display_date?: string | null;
    description?: string | null;
    formality_request?: {
      name?: string | null;
    } | null;
  },
>(
  resolutionList: T[],
  columnId: string,
  descending: boolean,
): T[] => {
  return [...resolutionList].sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    switch (columnId) {
      case "name":
        aValue = a.display_name?.toLowerCase() ?? "";
        bValue = b.display_name?.toLowerCase() ?? "";
        break;
      case "display_code":
        aValue = a.display_code ?? "";
        bValue = b.display_code ?? "";
        break;
      case "formality_request":
        aValue =
          a.formality_request?.name?.toLowerCase() ??
          a.description?.toLowerCase() ??
          "";
        bValue =
          b.formality_request?.name?.toLowerCase() ??
          b.description?.toLowerCase() ??
          "";
        break;
      case "elapsed_days":
        aValue = a.display_date ? new Date(a.display_date).getTime() : 0;
        bValue = b.display_date ? new Date(b.display_date).getTime() : 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return descending ? 1 : -1;
    if (aValue > bValue) return descending ? -1 : 1;
    return 0;
  });
};

export const getDefaultDetailsFilters = (
  status: string,
  isResolution: boolean,
) => {
  return isResolution
    ? [
        {
          id: 1,
          field: "updated_at",
          type: filterTypesEnum.dates,
          label: "Última actualización",
          shouldDisabledFutureDates: true,
          isUpdated: false,
          result: "",
        },
        {
          id: 2,
          field: "status",
          type: filterTypesEnum.status,
          label: "Estado",
          isUpdated: false,
          result: status,
          status: [
            {
              field: "APPROVED",
              label: "Aprobado",
              value: status.includes("APPROVED"),
            },
            {
              field: "DENIED",
              label: "Denegado",
              value: status.includes("DENIED"),
            },
            {
              field: "UNFAIR",
              label: "Improcedente",
              value: status.includes("UNFAIR"),
            },
            {
              field: "CANCELED",
              label: "Cancelado",
              value: status.includes("CANCELED"),
            },
          ],
        },
      ]
    : [
        {
          id: 1,
          field: "updated_at",
          type: filterTypesEnum.dates,
          label: "Última actualización",
          shouldDisabledFutureDates: true,
          isUpdated: false,
          result: "",
        },
        buildHierarchyStatusFilter(status) as any,
      ];
};
